import {
  Box,
  Card,
  Checkbox,
  Chip,
  Dialog,
  FormControl,
  InputLabel,
  MenuItem,
  Button as MUIButton,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { ReactElement, useState } from 'react';
import {
  BulkDeleteButton,
  CreateButton,
  Datagrid,
  EditButton,
  Filter,
  FunctionField,
  List,
  TextField,
  useListController,
  useTranslate,
  DeleteButton,
  SelectField,
  WithPermissions,
  Button,
  SelectInput,
} from 'react-admin';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionButton } from 'src/routes/components/permission-button';
import AddressField from 'src/pages/admin/components/AddressField';
import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Resources } from '../resource';
import CreateSmartMoney from './Create';
import EditSmartMoney from './Edit';
import { chains } from 'src/assets/data';
import UploadIcon from '@mui/icons-material/Upload';
import ImportSmartMoney from './Import';
import AuditLogDialog from '../../components/AuditLog/AuditLogDialog';
import { Iconify } from 'src/components/iconify';
import { XButton } from '../../components/XButton';
import FilterGroup from '../../components/FilterGroup';

const ListFilter = (props: any) => {
  const { filterValues } = useListController();

  return (
    <Filter {...(props || {})}>
      <FilterGroup alwaysOn spacing={0} p={0}>
        <FilterGroup>
          <SelectInput
            source="chainId"
            label="Chain"
            choices={[
              { id: 1, name: 'ETHEREUM' },
              { id: 56, name: 'BSC' },
            ]}
            defaultValue={501424}
            emptyText={'SOLANA'}
            emptyValue={501424}
            alwaysOn
          />
        </FilterGroup>
      </FilterGroup>
    </Filter>
  );
};

const View = () => {
  const [popup, setPopup] = useState<ReactElement>();
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditTitle, setAuditTitle] = useState('Audit Logs');
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const translate = useTranslate();
  const [sizeDialog, setSizeDialog] = useState<any>('md');

  return (
    <PermissionGuard resource="smart-money" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('resource.smartMoney')}
          links={[
            { name: translate('resource.smartMoney'), href: `/${Resources.smartMoney}` },
            { name: translate('resource.list') },
          ]}
          action={
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
              mb={1}
            >
              <PermissionGuard resource="smart-money" action="import" fallback={<></>}>
                <Button
                  label="Import"
                  variant="contained"
                  color="warning"
                  size="medium"
                  onClick={(e) => {
                    e.preventDefault();
                    setSizeDialog('sm');
                    setPopup(<ImportSmartMoney onClose={() => setPopup(undefined)} />);
                  }}
                  startIcon={<UploadIcon />}
                  sx={{
                    color: 'white',
                  }}
                />
              </PermissionGuard>
              <PermissionGuard resource="smart-money" action="create" fallback={<></>}>
                <CreateButton
                  variant="contained"
                  size="medium"
                  onClick={(e) => {
                    e.preventDefault();
                    setSizeDialog('md');
                    setPopup(<CreateSmartMoney onClose={() => setPopup(undefined)} />);
                  }}
                />
              </PermissionGuard>
              <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
                <Button
                  variant="contained"
                  size="medium"
                  color="secondary"
                  onClick={() => {
                    setSelectedRecord({ alias: 'Smart Money' });
                    setAuditTitle(`${translate('resource.auditLogs')} - ${translate('resource.smartMoney')}`);
                    setAuditLogOpen(true);
                  }}
                  label={translate('resource.auditLogs')}
                >
                  <Iconify icon="ph:note" />
                </Button>
              </PermissionGuard>
            </Stack>
          }
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            filters={<ListFilter />}
            perPage={10}
            exporter={false}
            actions={false}
            empty={false}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={
                  <PermissionButton resource="smart-money" action="delete">
                    <BulkDeleteButton />
                  </PermissionButton>
                }
                sx={{
                  '.RaBulkActionsToolbar-toolbar': {
                    position: 'relative !important',
                    transform: 'none !important',
                  },
                }}
              >
                {/* <FunctionField
                  label="module.smartMoney.avatar"
                  sortable={false}
                  render={(record: any) => {
                    const avatar = record?.avatar;
                    if (!avatar) return null;

                    return (
                      <img
                        src={avatar}
                        alt="avatar"
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          objectFit: 'cover',
                        }}
                      />
                    );
                  }}
                /> */}
                <TextField source="alias" label="module.smartMoney.alias" sortable={false} />
                {/* <TextField source="address" label="module.smartMoney.address" /> */}
                <FunctionField
                  label="module.smartMoney.address"
                  render={(record: any) => <AddressField address={record.address} />}
                  sortable={false}
                />

                <SelectField
                  source="chainId"
                  choices={chains}
                  label="module.smartMoney.chain"
                  sortable={false}
                />

                <FunctionField
                  label="module.smartMoney.enable"
                  render={(record) => (
                    <Chip
                      label={record.enable ? 'True' : 'False'}
                      color={record.enable ? 'success' : 'error'}
                      size="small"
                    />
                  )}
                  sortable={false}
                />

                <FunctionField
                  label="module.smartMoney.metadata"
                  textAlign="left"
                  minWidth={250}
                  sortable={false}
                  render={(record: any) => {
                    const { metadata } = record;
                    if (!metadata) return '';

                    const jsonString = JSON.stringify(metadata, null, 2);

                    return (
                      <Tooltip title={jsonString} arrow>
                        <Typography
                          sx={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: '0.875rem',
                          }}
                        >
                          {jsonString}
                        </Typography>
                      </Tooltip>
                    );
                  }}
                />

                <FunctionField
                  render={(record: any) => (
                    <Stack direction="row" spacing={2}>
                      <PermissionGuard resource="smart-money" action="update" fallback={<></>}>
                        <EditButton
                          onClick={(e) => {
                            e.preventDefault();
                            setSizeDialog('md');
                            setPopup(
                              <EditSmartMoney id={record.id} onClose={() => setPopup(undefined)} />
                            );
                          }}
                        />
                      </PermissionGuard>
                      <PermissionGuard resource="smart-money" action="delete" fallback={<></>}>
                        <DeleteButton mutationMode="pessimistic" />
                      </PermissionGuard>
                      <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
                        <XButton
                          variant="text"
                          size="medium"
                          color="secondary"
                          disabled={!record.hasAuditLog}
                          onClick={() => {
                            setSelectedRecord(record);
                            setAuditTitle(`${translate('resource.auditLogs')} - ${record.alias} (#${record.id})`);
                            setAuditLogOpen(true);
                          }}
                          label={translate('resource.auditLogs')}
                        >
                          <Iconify icon="ph:note" />
                        </XButton>
                      </PermissionGuard>
                    </Stack>
                  )}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>
        <Dialog
          open={Boolean(popup)}
          onClose={() => setPopup(undefined)}
          maxWidth={sizeDialog}
          fullWidth
        >
          {popup}
        </Dialog>
        <AuditLogDialog
          open={auditLogOpen}
          onClose={() => setAuditLogOpen(false)}
          resource={Resources.smartMoney}
          recordId={selectedRecord?.id}
          title={auditTitle}
        />
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
